/**
 * Priority fair outbound queue for rpapp-customer HTTP (Wave D storm dual-defense).
 * Decides *when* a call may start; singleflight / coordinators remain inside the slot.
 * Storm latch freezes starts via {@link freezeCustomerOutboundForStorm}.
 *
 * Spacing/depth only ({@link HOT_PATH_MIN_INTERVAL_MS}, lane depths). Durable guest
 * silence retired — auth-lane is no longer refuse-coupled to guest latch.
 */

import {
  getHttpStormRetryAfterMs,
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from './httpStormDetectedLatch.js';

/** Hard cap on concurrent started requests (all lanes). */
export const GLOBAL_MAX_INFLIGHT = 6;
/**
 * Interactive may hold at most this many when any non-interactive waiter exists
 * (reserves K=1 global slot for auth/realtime/default).
 */
export const INTERACTIVE_MAX_INFLIGHT = 5;
/** Non-interactive waiters aged ≥ this become eligible for the reserved slot under interactive flood. */
export const LANE_AGE_BOOST_MS = 2000;

/** Hot-path min start interval (/me) — G7 B1 parity with admin. */
export const HOT_PATH_MIN_INTERVAL_MS = 2000;
/** Default lane min start interval — G7 budget. */
export const DEFAULT_MIN_INTERVAL_MS = 250;
/** Overflow retryAfterMs for interactive (no min interval beyond global). */
export const INTERACTIVE_OVERFLOW_RETRY_AFTER_MS = 250;

export const INTERACTIVE_QUEUE_DEPTH = 32;
export const NON_INTERACTIVE_QUEUE_DEPTH = 8;

export const CUSTOMER_OUTBOUND_QUEUE_OVERFLOW_CODE = 'CUSTOMER_OUTBOUND_QUEUE_OVERFLOW' as const;

export type CustomerOutboundLane = 'interactive' | 'auth' | 'realtime' | 'default';

export class CustomerOutboundFairQueueOverflowError extends Error {
  public readonly code = CUSTOMER_OUTBOUND_QUEUE_OVERFLOW_CODE;
  public readonly sourceKey: string;
  public readonly retryAfterMs: number;

  public constructor(sourceKey: string, retryAfterMs: number) {
    // Technical English for logs / diagnostics only — never put this string on
    // ApiError.message for UI (ApiClient / platformAccountFetch remap to i18n).
    super(
      `Customer outbound fair queue overflow for ${sourceKey}. Retry after ${String(retryAfterMs)}ms.`,
    );
    this.name = 'CustomerOutboundFairQueueOverflowError';
    this.sourceKey = sourceKey;
    this.retryAfterMs = retryAfterMs;
  }
}

const LANE_PRIORITY: readonly CustomerOutboundLane[] = [
  'interactive',
  'auth',
  'realtime',
  'default',
];

const NON_INTERACTIVE_LANES: readonly CustomerOutboundLane[] = ['auth', 'realtime', 'default'];

interface Waiter {
  readonly sourceKey: string;
  readonly lane: CustomerOutboundLane;
  readonly enqueuedAt: number;
  readonly signal: AbortSignal | undefined;
  readonly onStart: (() => void) | undefined;
  readonly run: () => Promise<unknown>;
  readonly resolve: (value: unknown) => void;
  readonly reject: (reason: unknown) => void;
  abortHandler: (() => void) | undefined;
}

interface LaneState {
  rrOrder: string[];
  bySource: Map<string, Waiter[]>;
}

interface QueueState {
  lanes: Record<CustomerOutboundLane, LaneState>;
  globalInflight: number;
  interactiveInflight: number;
  sourceInflight: Map<string, number>;
  lastStartAt: Map<string, number>;
  wakeTimer: ReturnType<typeof setTimeout> | undefined;
  nextWakeAt: number | undefined;
  /** Exclusive upper bound (Date.now() ms) while storm freeze is active; 0 = not frozen. */
  stormFrozenUntilMs: number;
}

function createEmptyLane(): LaneState {
  return { rrOrder: [], bySource: new Map() };
}

function createInitialState(): QueueState {
  return {
    lanes: {
      interactive: createEmptyLane(),
      auth: createEmptyLane(),
      realtime: createEmptyLane(),
      default: createEmptyLane(),
    },
    globalInflight: 0,
    interactiveInflight: 0,
    sourceInflight: new Map(),
    lastStartAt: new Map(),
    wakeTimer: undefined,
    nextWakeAt: undefined,
    stormFrozenUntilMs: 0,
  };
}

let state: QueueState = createInitialState();

/**
 * Deterministic client source key from method + path (customer / platform-chrome paths).
 */
export function normalizeCustomerOutboundSourceKey(method: string, path: string): string {
  const upperMethod = method.toUpperCase();

  let normalized = path;
  const queryIdx = normalized.indexOf('?');
  if (queryIdx >= 0) {
    normalized = normalized.slice(0, queryIdx);
  }
  const hashIdx = normalized.indexOf('#');
  if (hashIdx >= 0) {
    normalized = normalized.slice(0, hashIdx);
  }

  if (/^https?:\/\//i.test(normalized)) {
    try {
      normalized = new URL(normalized).pathname;
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      // Log/diagnostic throw — callers that surface UI must remap (not ApiError).
      throw new Error(`Failed to parse absolute URL for outbound source key: ${detail}`);
    }
  }

  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }

  // Tenant-prefixed product shape: /api/{tenant}/v1/customer-account|...
  const tenantV1 =
    /^\/api\/([^/]+)\/v1\/(customer-account|customer-auth|payments|receipts)\//.exec(
      normalized,
    );
  if (
    tenantV1 !== null &&
    tenantV1[1] !== 'v1' &&
    tenantV1[1] !== 'customer-account' &&
    tenantV1[1] !== 'customer-auth'
  ) {
    normalized = normalized.replace(/^\/api\/[^/]+\/v1/, '');
  } else {
    // Legacy alternate: /api/v1/{tenant}/customer-account|...
    const tenantPrefixed =
      /^\/api\/v1\/([^/]+)\/(customer-account|customer-auth|payments|receipts)\//.exec(
        normalized,
      );
    if (
      tenantPrefixed !== null &&
      tenantPrefixed[1] !== 'customer-account' &&
      tenantPrefixed[1] !== 'customer-auth' &&
      tenantPrefixed[1] !== 'payments' &&
      tenantPrefixed[1] !== 'receipts'
    ) {
      normalized = normalized.replace(/^\/api\/v1\/[^/]+/, '');
    } else if (normalized.startsWith('/api/v1/')) {
      normalized = normalized.slice('/api/v1'.length);
    } else if (normalized.startsWith('/api/')) {
      normalized = normalized.slice('/api'.length);
    }
  }

  normalized = normalized.replace(/\/{2,}/g, '/');
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return `${upperMethod}:${normalized}`;
}

/**
 * Auth-hot keys: platform/tenant `/me` + refresh (auth lane, not interactive).
 * Guest-latch enqueue refuse retired — these keys still share auth-lane spacing.
 */
export function isCustomerAuthHotSourceKey(sourceKey: string): boolean {
  return (
    sourceKey === 'GET:/customer-account/me' ||
    sourceKey === 'GET:/customer-account/platform/me' ||
    sourceKey === 'POST:/customer-auth/refresh'
  );
}

export function laneForCustomerOutboundSourceKey(sourceKey: string): CustomerOutboundLane {
  // G8/G10 — auth-hot before method→interactive so refresh freezes with /me.
  if (isCustomerAuthHotSourceKey(sourceKey)) {
    return 'auth';
  }
  const colon = sourceKey.indexOf(':');
  const method = colon >= 0 ? sourceKey.slice(0, colon) : sourceKey;
  if (method !== 'GET') {
    return 'interactive';
  }
  // Checkout / FIO-style polls — treat as realtime hot path when present.
  if (
    sourceKey.includes('/payments/') ||
    sourceKey.includes('/pickup/') ||
    sourceKey.endsWith('/status')
  ) {
    return 'realtime';
  }
  return 'default';
}

function queueDepthForLane(lane: CustomerOutboundLane): number {
  return lane === 'interactive' ? INTERACTIVE_QUEUE_DEPTH : NON_INTERACTIVE_QUEUE_DEPTH;
}

export function minIntervalMsForCustomerSourceKey(sourceKey: string): number {
  const lane = laneForCustomerOutboundSourceKey(sourceKey);
  if (lane === 'auth' || lane === 'realtime') {
    return HOT_PATH_MIN_INTERVAL_MS;
  }
  if (lane === 'default') {
    return DEFAULT_MIN_INTERVAL_MS;
  }
  return 0;
}

function overflowRetryAfterMs(sourceKey: string): number {
  const lane = laneForCustomerOutboundSourceKey(sourceKey);
  if (lane === 'interactive') {
    return INTERACTIVE_OVERFLOW_RETRY_AFTER_MS;
  }
  return minIntervalMsForCustomerSourceKey(sourceKey);
}

function createAbortError(options?: { readonly neverStarted?: boolean }): Error {
  const neverStarted = options?.neverStarted === true;
  let err: Error;
  if (typeof DOMException !== 'undefined') {
    err = new DOMException('The operation was aborted.', 'AbortError');
  } else {
    err = new Error('The operation was aborted.');
    err.name = 'AbortError';
  }
  if (neverStarted) {
    Object.defineProperty(err, 'customerOutboundNeverStarted', {
      value: true,
      enumerable: true,
    });
  }
  return err;
}

/** True when fair-queue rejected because AbortSignal fired while still queued (no start). */
export function isCustomerOutboundQueuedAbort(error: unknown): boolean {
  if (error == null || typeof error !== 'object') {
    return false;
  }
  const name = 'name' in error && typeof error.name === 'string' ? error.name : '';
  if (name !== 'AbortError') {
    return false;
  }
  return (
    'customerOutboundNeverStarted' in error &&
    (error as { customerOutboundNeverStarted?: unknown }).customerOutboundNeverStarted === true
  );
}

/**
 * Freeze queue starts until `Date.now() + retryAfterMs` (extends if already frozen longer).
 * Inflight calls finish; waiters stay queued; pump resumes when freeze expires.
 */
export function freezeCustomerOutboundForStorm(retryAfterMs: number): void {
  const ms = Math.max(0, Math.floor(retryAfterMs));
  const until = Date.now() + ms;
  if (until > state.stormFrozenUntilMs) {
    state.stormFrozenUntilMs = until;
  }
  scheduleWakeAt(state.stormFrozenUntilMs);
}

/**
 * True when storm latch (or residual timestamp freeze) blocks fair-queue starts.
 * Live latch is SoT (admin twin) so TTL expiry unblocks without notify-on-expire.
 */
export function isCustomerOutboundStormFrozen(): boolean {
  // Live latch — pause queued starts for the full latch TTL.
  if (isHttpStormLatched()) {
    return true;
  }
  if (state.stormFrozenUntilMs <= 0) {
    return false;
  }
  if (Date.now() >= state.stormFrozenUntilMs) {
    state.stormFrozenUntilMs = 0;
    return false;
  }
  return true;
}

/** Test-only: clear storm freeze without resetting the whole queue. */
export function clearCustomerOutboundStormFreezeForTests(): void {
  state.stormFrozenUntilMs = 0;
}

function waitingCountForSource(lane: CustomerOutboundLane, sourceKey: string): number {
  return state.lanes[lane].bySource.get(sourceKey)?.length ?? 0;
}

function hasAnyWaiters(lane: CustomerOutboundLane): boolean {
  return state.lanes[lane].rrOrder.length > 0;
}

function hasNonInteractiveWaiters(): boolean {
  return NON_INTERACTIVE_LANES.some((lane) => hasAnyWaiters(lane));
}

function peekHeadWaiter(lane: CustomerOutboundLane, sourceKey: string): Waiter | undefined {
  const queue = state.lanes[lane].bySource.get(sourceKey);
  return queue?.[0];
}

function enqueueWaiter(waiter: Waiter): void {
  const laneState = state.lanes[waiter.lane];
  let queue = laneState.bySource.get(waiter.sourceKey);
  if (!queue) {
    queue = [];
    laneState.bySource.set(waiter.sourceKey, queue);
    laneState.rrOrder.push(waiter.sourceKey);
  }
  queue.push(waiter);
}

function removeWaiterFromQueue(waiter: Waiter): boolean {
  const laneState = state.lanes[waiter.lane];
  const queue = laneState.bySource.get(waiter.sourceKey);
  if (!queue) {
    return false;
  }
  const idx = queue.indexOf(waiter);
  if (idx < 0) {
    return false;
  }
  queue.splice(idx, 1);
  if (queue.length === 0) {
    laneState.bySource.delete(waiter.sourceKey);
    const rrIdx = laneState.rrOrder.indexOf(waiter.sourceKey);
    if (rrIdx >= 0) {
      laneState.rrOrder.splice(rrIdx, 1);
    }
  }
  return true;
}

function rotateSourceToBack(lane: CustomerOutboundLane, sourceKey: string): void {
  const laneState = state.lanes[lane];
  const rrIdx = laneState.rrOrder.indexOf(sourceKey);
  if (rrIdx < 0) {
    return;
  }
  laneState.rrOrder.splice(rrIdx, 1);
  if (laneState.bySource.has(sourceKey)) {
    laneState.rrOrder.push(sourceKey);
  }
}

function sourceInflightCount(sourceKey: string): number {
  return state.sourceInflight.get(sourceKey) ?? 0;
}

function canStartSource(sourceKey: string, now: number): boolean {
  if (sourceInflightCount(sourceKey) > 0) {
    return false;
  }
  const minInterval = minIntervalMsForCustomerSourceKey(sourceKey);
  if (minInterval <= 0) {
    return true;
  }
  const last = state.lastStartAt.get(sourceKey);
  if (last === undefined) {
    return true;
  }
  return now - last >= minInterval;
}

function msUntilSourceEligible(sourceKey: string, now: number): number {
  const minInterval = minIntervalMsForCustomerSourceKey(sourceKey);
  if (minInterval <= 0) {
    return 0;
  }
  const last = state.lastStartAt.get(sourceKey);
  if (last === undefined) {
    return 0;
  }
  return Math.max(0, minInterval - (now - last));
}

function pickRoundRobinEligible(
  lane: CustomerOutboundLane,
  now: number,
): Waiter | undefined {
  const laneState = state.lanes[lane];
  if (laneState.rrOrder.length === 0) {
    return undefined;
  }
  for (let i = 0; i < laneState.rrOrder.length; i += 1) {
    const sourceKey = laneState.rrOrder[i];
    if (sourceKey === undefined) {
      continue;
    }
    if (!canStartSource(sourceKey, now)) {
      continue;
    }
    const head = peekHeadWaiter(lane, sourceKey);
    if (head) {
      return head;
    }
  }
  return undefined;
}

function pickAgedNonInteractive(now: number): Waiter | undefined {
  for (const lane of NON_INTERACTIVE_LANES) {
    const laneState = state.lanes[lane];
    for (const sourceKey of laneState.rrOrder) {
      const head = peekHeadWaiter(lane, sourceKey);
      if (!head) {
        continue;
      }
      if (now - head.enqueuedAt < LANE_AGE_BOOST_MS) {
        continue;
      }
      if (!canStartSource(sourceKey, now)) {
        continue;
      }
      return head;
    }
  }
  return undefined;
}

function clearWakeTimer(): void {
  if (state.wakeTimer !== undefined) {
    clearTimeout(state.wakeTimer);
    state.wakeTimer = undefined;
    state.nextWakeAt = undefined;
  }
}

function scheduleWakeAt(atMs: number): void {
  const now = Date.now();
  if (atMs <= now) {
    clearWakeTimer();
    queueMicrotask(() => {
      pump();
    });
    return;
  }
  if (state.nextWakeAt !== undefined && state.nextWakeAt <= atMs) {
    return;
  }
  clearWakeTimer();
  state.nextWakeAt = atMs;
  const delay = Math.max(0, atMs - now);
  state.wakeTimer = setTimeout(() => {
    state.wakeTimer = undefined;
    state.nextWakeAt = undefined;
    pump();
  }, delay);
}

function considerWakeForPending(now: number): void {
  let earliest: number | undefined;
  if (state.stormFrozenUntilMs > now) {
    earliest = state.stormFrozenUntilMs;
  }
  for (const lane of LANE_PRIORITY) {
    const laneState = state.lanes[lane];
    for (const sourceKey of laneState.rrOrder) {
      if (sourceInflightCount(sourceKey) > 0) {
        continue;
      }
      const wait = msUntilSourceEligible(sourceKey, now);
      if (wait <= 0) {
        continue;
      }
      const at = now + wait;
      if (earliest === undefined || at < earliest) {
        earliest = at;
      }
    }
    if (lane !== 'interactive') {
      for (const sourceKey of laneState.rrOrder) {
        const head = peekHeadWaiter(lane, sourceKey);
        if (!head) {
          continue;
        }
        const boostAt = head.enqueuedAt + LANE_AGE_BOOST_MS;
        if (boostAt > now && (earliest === undefined || boostAt < earliest)) {
          earliest = boostAt;
        }
      }
    }
  }
  if (earliest !== undefined) {
    scheduleWakeAt(earliest);
  }
}

function detachAbort(waiter: Waiter): void {
  const { signal, abortHandler } = waiter;
  if (signal && abortHandler) {
    signal.removeEventListener('abort', abortHandler);
  }
}

function startWaiter(waiter: Waiter): void {
  const now = Date.now();
  const removed = removeWaiterFromQueue(waiter);
  if (!removed) {
    return;
  }
  detachAbort(waiter);
  rotateSourceToBack(waiter.lane, waiter.sourceKey);

  state.lastStartAt.set(waiter.sourceKey, now);
  state.globalInflight += 1;
  state.sourceInflight.set(waiter.sourceKey, sourceInflightCount(waiter.sourceKey) + 1);
  if (waiter.lane === 'interactive') {
    state.interactiveInflight += 1;
  }

  const finish = (): void => {
    state.globalInflight = Math.max(0, state.globalInflight - 1);
    if (waiter.lane === 'interactive') {
      state.interactiveInflight = Math.max(0, state.interactiveInflight - 1);
    }
    const nextCount = sourceInflightCount(waiter.sourceKey) - 1;
    if (nextCount <= 0) {
      state.sourceInflight.delete(waiter.sourceKey);
    } else {
      state.sourceInflight.set(waiter.sourceKey, nextCount);
    }
    pump();
  };

  try {
    waiter.onStart?.();
  } catch (onStartError: unknown) {
    finish();
    waiter.reject(onStartError);
    return;
  }

  void Promise.resolve()
    .then(() => {
      return waiter.run();
    })
    .then(
      (value) => {
        finish();
        waiter.resolve(value);
      },
      (reason: unknown) => {
        finish();
        waiter.reject(reason);
      },
    );
}

function pump(): void {
  if (isCustomerOutboundStormFrozen()) {
    const remaining = getHttpStormRetryAfterMs();
    if (remaining > 0) {
      scheduleWakeAt(Date.now() + remaining);
    } else if (state.stormFrozenUntilMs > Date.now()) {
      scheduleWakeAt(state.stormFrozenUntilMs);
    } else {
      clearWakeTimer();
    }
    return;
  }

  const now = Date.now();

  while (state.globalInflight < GLOBAL_MAX_INFLIGHT) {
    const nonInteractiveWaiting = hasNonInteractiveWaiters();
    const maxInteractive = nonInteractiveWaiting
      ? INTERACTIVE_MAX_INFLIGHT
      : GLOBAL_MAX_INFLIGHT;
    const interactiveAlsoWaiting = hasAnyWaiters('interactive');

    const aged = pickAgedNonInteractive(now);
    if (aged && interactiveAlsoWaiting) {
      startWaiter(aged);
      continue;
    }

    if (state.interactiveInflight < maxInteractive) {
      const interactiveNext = pickRoundRobinEligible('interactive', now);
      if (interactiveNext) {
        startWaiter(interactiveNext);
        continue;
      }
    }

    let startedNonInteractive = false;
    for (const lane of NON_INTERACTIVE_LANES) {
      const next = pickRoundRobinEligible(lane, now);
      if (next) {
        startWaiter(next);
        startedNonInteractive = true;
        break;
      }
    }
    if (startedNonInteractive) {
      continue;
    }

    break;
  }

  if (
    state.globalInflight < GLOBAL_MAX_INFLIGHT &&
    LANE_PRIORITY.some((lane) => hasAnyWaiters(lane))
  ) {
    considerWakeForPending(now);
  } else {
    clearWakeTimer();
  }
}

export interface ScheduleCustomerOutboundOptions<T> {
  readonly method: string;
  readonly path: string;
  readonly signal?: AbortSignal;
  /** Invoked exactly when the fair-queue start is consumed (before `run`). */
  readonly onStart?: () => void;
  readonly run: () => Promise<T>;
}

/**
 * Schedule an outbound customer HTTP call through the priority fair queue.
 * Primary entry (admin twin: `scheduleAdminOutbound`).
 */
export function runWithCustomerOutboundFairQueue<T>(
  opts: ScheduleCustomerOutboundOptions<T>,
): Promise<T> {
  const sourceKey = normalizeCustomerOutboundSourceKey(opts.method, opts.path);
  const lane = laneForCustomerOutboundSourceKey(sourceKey);

  if (opts.signal?.aborted) {
    return Promise.reject(createAbortError({ neverStarted: true }));
  }

  const depth = queueDepthForLane(lane);
  if (waitingCountForSource(lane, sourceKey) >= depth) {
    throw new CustomerOutboundFairQueueOverflowError(sourceKey, overflowRetryAfterMs(sourceKey));
  }

  return new Promise<T>((resolve, reject) => {
    const waiter: Waiter = {
      sourceKey,
      lane,
      enqueuedAt: Date.now(),
      signal: opts.signal,
      onStart: opts.onStart,
      run: opts.run as () => Promise<unknown>,
      resolve: (value: unknown) => {
        resolve(value as T);
      },
      reject,
      abortHandler: undefined,
    };

    if (opts.signal) {
      const onAbort = (): void => {
        const wasQueued = removeWaiterFromQueue(waiter);
        detachAbort(waiter);
        if (wasQueued) {
          reject(createAbortError({ neverStarted: true }));
          pump();
        }
      };
      waiter.abortHandler = onAbort;
      opts.signal.addEventListener('abort', onAbort, { once: true });
    }

    if (waitingCountForSource(lane, sourceKey) >= depth) {
      detachAbort(waiter);
      reject(new CustomerOutboundFairQueueOverflowError(sourceKey, overflowRetryAfterMs(sourceKey)));
      return;
    }

    enqueueWaiter(waiter);
    pump();
  });
}

/** Alias of `runWithCustomerOutboundFairQueue` (enqueue naming for wire surfaces). */
export function enqueueCustomerOutbound<T>(
  opts: ScheduleCustomerOutboundOptions<T>,
): Promise<T> {
  return runWithCustomerOutboundFairQueue(opts);
}

/** Test-only: reset module queue state between cases. */
export function __resetCustomerOutboundFairQueueForTests(): void {
  clearWakeTimer();
  state = createInitialState();
}

// Wave D — latch bus freezes fair-queue starts (extends until remaining latch TTL).
// When latch clears, pump resumes so queued waiters can start.
subscribeHttpStormLatch(() => {
  if (isHttpStormLatched()) {
    freezeCustomerOutboundForStorm(getHttpStormRetryAfterMs());
  } else {
    state.stormFrozenUntilMs = 0;
    queueMicrotask(() => {
      pump();
    });
  }
});
if (isHttpStormLatched()) {
  freezeCustomerOutboundForStorm(getHttpStormRetryAfterMs());
}
